import { toast } from 'react-toastify'

import {
  useGetCampaignReplies,
  useGetCampaignSequence,
  useSyncCampaignReplies,
} from '../sequences_api'
import { RepliesTable } from './RepliesTable'

/**
 * ==================== REPLIES TAB ====================
 * Displays replies synced from EmailBison and stored locally.
 *
 * Unlike Scheduled Emails (passthrough API), replies are stored in our
 * local database and kept up-to-date via webhooks. This approach is used
 * because:
 * - Webhook events trigger sync for new replies
 * - Local storage allows for faster queries and filtering
 * - Manual sync button handles edge cases where webhooks may have missed events
 */

interface RepliesTabProps {
  campaignId: number
}

export function RepliesTab({ campaignId }: RepliesTabProps) {
  // Get sequence to access bison_campaign_id
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)

  const bisonCampaignId = sequence?.bison_campaign_id

  const {
    data: repliesData,
    isLoading: isLoadingReplies,
    isFetching,
  } = useGetCampaignReplies(bisonCampaignId || 0)

  const syncMutation = useSyncCampaignReplies(bisonCampaignId || 0)

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced ${result.synced} replies (${result.created} new, ${result.updated} updated)`
      )
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Failed to sync replies'
      toast.error(errorMessage)
    }
  }

  if (isLoadingSequence || (isLoadingReplies && !repliesData)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!bisonCampaignId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No EmailBison campaign found</div>
      </div>
    )
  }

  if (repliesData && repliesData.count === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="text-gray-400 mb-2">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Replies</h3>
        <p className="text-sm text-gray-500 max-w-sm mb-4">
          Replies will appear here once leads start responding to your campaign
          emails.
        </p>
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncMutation.isPending ? (
            <>
              <svg
                className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Syncing...
            </>
          ) : (
            <>
              <svg
                className="-ml-0.5 mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Sync Replies
            </>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sync button header */}
      <div className="flex justify-end items-center px-4 py-2 border-b border-gray-200">
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncMutation.isPending ? (
            <>
              <svg
                className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Syncing...
            </>
          ) : (
            <>
              <svg
                className="-ml-0.5 mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Sync Replies
            </>
          )}
        </button>
      </div>
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <RepliesTable
          data={repliesData}
          isLoading={isFetching}
          bisonCampaignId={bisonCampaignId || 0}
        />
      </div>
    </div>
  )
}
